import {Hash} from '@/utils/portuna/dataPool';
import {reactive} from 'vue';
import {Component, Vue, Watch} from 'vue-facing-decorator';
import LocalStorage from '../library/localStorage';

/** @deprecated convert to "Setup"-API and use "searchAbstract"-Composable instead **/
@Component({
    name: 'SearchAbstract',
})
export class SearchAbstract extends Vue
{
    isSearchAbstract = true;

    entriesPerPage = 25;

    search: SearchDefinition = reactive({});

    sortField: string | string[] = '';

    orderBy: FilterOrder = 'asc';

    searchWatcherEnabled: boolean = true;

    @Watch('entriesPerPage')
    watchEntriesPerPage()
    {
        this.updateLocalStorage();
    }

    @Watch('searchStorageScope')
    watchLocalStorageScope()
    {
        this.search = reactive({}); // not reactive
        this.initSearch();
        this.sortField = '';
        this.orderBy   = 'asc';
    }

    @Watch('search', {deep: true})
    watchSearchAndFetchList()
    {
        if (!this.searchWatcherEnabled)
        {
            return;
        }

        this.searchWatcherEnabled = false; // prevent preSearch() from triggering this watcher
        this.preSearch(this.search);

        this.$nextTick()
        .then(() =>
        {
            this.searchWatcherEnabled = true; // enable watcher again
            this.updateLocalStorage();
        });
    }

    @Watch('sortField')
    @Watch('orderBy')
    watchSortAndOrderBy()
    {
        this.emitter.emit('setSort', this.sortField, this.orderBy);
        this.updateLocalStorage();
    }

    get searchStorageScope(): string | boolean
    {
        return '';
    }

    get currentScope()
    {
        if (false === this.searchStorageScope)
        {
            return false;
        }

        return this.searchStorageScope ||
            (this.$route.fullPath || window.location.pathname).split('/').filter((v) => !!v).slice(0, 3).join('-');
    }

    get searchProperties()
    {
        return {
            currentScope: this.currentScope,
            defaultEntriesPerPage: this.entriesPerPage,
            localStorageScope: this.searchStorageScope,
            orderBy: this.orderBy,
            search: this.search,
            setSort: this.setSort,
            sortField: this.sortField,
            setEntriesPerPage: this.setEntriesPerPage,
        };
    }

    created()
    {
        this.initSearchComponent();
    }

    beforeUnmount()
    {
        this.destroySearchComponent();
    }

    hashChanged()
    {
        // do not change this.search directly
        let search: SearchDefinition = Object.assign({}, this.search);

        search = this.applySearchHash(search);

        this.search = reactive(search);
    }

    getLocalStorage(): { search: SearchDefinition, order: { key?: string | string[], direction?: FilterOrder }, entriesPerPage: number }
    {
        if (!this.currentScope)
        {
            return {
                search: {},
                order: {},
                entriesPerPage: this.entriesPerPage,
            };
        }

        return {
            search: LocalStorage.get('search.' + this.currentScope, {}),
            order: LocalStorage.get('order.' + this.currentScope, {}),
            entriesPerPage: LocalStorage.get('entriesPerPage.' + this.currentScope, this.entriesPerPage),
        };
    }

    preInitSearch(search: SearchDefinition): void
    {
        // overwrite in component
    }

    postInitSearch(search: SearchDefinition): void
    {
        // overwrite in component
    }

    postInitOrder(order: OrderDefinition): OrderDefinition
    {
        // overwrite in component
        return order;
    }

    preSearch(search: SearchDefinition): void
    {
        // overwrite in component
    }

    initSearchComponent()
    {
        this.initSearch();
        window.addEventListener('hashchange', this.hashChanged);
    }

    destroySearchComponent()
    {
        window.removeEventListener('hashchange', this.hashChanged);
    }

    applySearchHash(search: SearchDefinition)
    {
        if (!this.currentScope)
        {
            return search;
        }

        Hash.rebuild();
        let searchHash: SearchDefinition | undefined = Hash.get('search');

        if (searchHash && 'object' === typeof searchHash)
        {
            // overwrite is set -> ignore everything not from hash
            if (searchHash.ovr)
            {
                search = searchHash;
                delete search.ovr; // prevent search for ovr-key
            }
            else
            {
                Object.assign(search, searchHash);
            }
        }

        return search;
    }

    initSearch()
    {
        // do not change this.search directly
        let search: SearchDefinition = Object.assign({}, this.search);
        let localStorage             = this.getLocalStorage();

        this.preInitSearch(search);

        // apply local storage
        if (localStorage.search)
        {
            Object.assign(search, localStorage.search);
        }

        // apply order
        if (localStorage.order && localStorage.order.key)
        {
            this.orderBy   = localStorage.order.direction as FilterOrder;
            this.sortField = localStorage.order.key;
        }

        search = this.applySearchHash(search);

        this.postInitSearch(search);

        const order = this.postInitOrder({
            field: this.sortField,
            orderBy: this.orderBy,
        });
        if (order)
        {
            this.sortField = order.field;
            this.orderBy   = order.orderBy;
        }

        this.preSearch(search);

        this.search = reactive(search);

        // set entries per page
        if (localStorage.entriesPerPage > this.entriesPerPage)
        {
            this.entriesPerPage = localStorage.entriesPerPage;
        }
    }

    updateLocalStorage()
    {
        if (!this.currentScope)
        {
            return;
        }

        LocalStorage.set('search.' + this.currentScope, this.search);
        LocalStorage.set('entriesPerPage.' + this.currentScope, this.entriesPerPage);
        LocalStorage.set(
            'order.' + this.currentScope,
            {
                key: Array.isArray(this.sortField) ? Array.from(this.sortField) : this.sortField,
                direction: this.orderBy,
            },
        );
    }

    setSort(field: string | string[], order: FilterOrder): void
    {
        this.sortField = field;
        this.orderBy   = order;
    }

    setEntriesPerPage(entriesPerPage: number): void
    {
        this.entriesPerPage = entriesPerPage;
    }
}